// Fleet-wide terraform module inventory: every terraform job's getStackModules
// rows rolled up per module, per version and per provider. A module's "latest"
// is its newest release in the Spacelift registry (getModuleVersions) when the
// registry answered for it, otherwise the newest version deployed anywhere.

import { ModuleVersionsEntry, TerraformStack } from '../../types/DeploymentRecord';
import { providerLabel } from './stackModules';

export interface JobStacks {
  app: string;
  jobId: string;
  stacks: TerraformStack[];
}

/** How far a deployed version trails its module's latest. */
export type Lag = 'current' | 'patch' | 'minor' | 'major' | 'unknown';

export const LAG_ORDER: Lag[] = ['current', 'patch', 'minor', 'major', 'unknown'];

/** One module call in one env/region of one job. */
export interface ModuleUsage {
  app: string;
  jobId: string;
  envRegion: string;
  key: string;
  version: string;
  constraint: string;
  declaredIn: string;
  /** Called from the stack's root, not from inside another module. */
  direct: boolean;
  /** The `//sub/path` of a submodule call, "" for the module root. */
  subpath: string;
  lag: Lag;
  appliedAt: string;
}

export interface VersionBucket {
  version: string;
  usages: number;
  /** Distinct app/job pairs on this version. */
  jobs: number;
  lag: Lag;
}

export interface ModuleSummary {
  /** Registry address without the `//subpath`. */
  source: string;
  name: string;
  usages: ModuleUsage[];
  directUsages: number;
  /** Distinct `app/job` keys. */
  jobs: string[];
  apps: string[];
  /** Deployed versions, newest first. */
  versions: VersionBucket[];
  /** What lag is measured against: the registry's latest, else newestDeployed. */
  newest: string;
  newestDeployed: string;
  latestSource: 'registry' | 'deployed';
  /** Published versions from the registry, newest first; [] when unknown. */
  published: string[];
  /** Why the registry gave no answer, when it was asked and didn't. */
  registryError?: string;
  lagCounts: Record<Lag, number>;
  constraints: string[];
  subpaths: string[];
  /** Modules whose code calls this one. */
  parents: string[];
  /** Modules this one's code calls. */
  children: string[];
}

export interface ProviderSummary {
  address: string;
  label: string;
  /** Newest first; counts are stack rows (one per job env/region). */
  versions: Array<{ version: string; stacks: number; lag: Lag }>;
  newest: string;
  stacks: number;
  jobs: number;
}

/** A job whose env/regions run different versions of the same module call. */
export interface EnvDrift {
  app: string;
  jobId: string;
  key: string;
  source: string;
  byEnv: Array<{ envRegion: string; version: string }>;
}

export interface ModuleEdge {
  parent: string;
  child: string;
  /** Distinct jobs where the parent calls the child. */
  jobs: number;
}

export interface FleetStats {
  jobs: number;
  stackRows: number;
  envRegions: number;
  modules: number;
  calls: number;
  directCalls: number;
  localCalls: number;
  /** Share of direct registry calls on their module's newest version, 0..1. */
  currentShare: number;
  /** Modules with more than one version deployed. */
  fragmented: number;
  /** Modules whose latest came from the registry. */
  registryModules: number;
}

export interface FleetModules {
  modules: ModuleSummary[];
  providers: ProviderSummary[];
  drift: EnvDrift[];
  edges: ModuleEdge[];
  stats: FleetStats;
  /** Direct registry calls by lag. */
  lagTotals: Record<Lag, number>;
}

// ---------------------------------------------------------------------------
// Versions
// ---------------------------------------------------------------------------

interface ParsedVersion {
  parts: [number, number, number];
  pre: string;
}

// Build metadata (+…) doesn't take part in ordering, so it is dropped.
const VERSION_RE = /^v?(\d+)(?:\.(\d+))?(?:\.(\d+))?([^+]*)(?:\+.*)?$/;

export const parseVersion = (v: string): ParsedVersion | null => {
  const m = VERSION_RE.exec(v.trim());
  if (!m) return null;
  return { parts: [Number(m[1]), Number(m[2] || 0), Number(m[3] || 0)], pre: m[4] || '' };
};

/** Descending-friendly semver compare; unparsable versions sort below parsable ones. */
export const compareVersions = (a: string, b: string): number => {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  if (!pa || !pb) return pa ? 1 : pb ? -1 : a.localeCompare(b);
  for (let i = 0; i < 3; i++) {
    if (pa.parts[i] !== pb.parts[i]) return pa.parts[i] - pb.parts[i];
  }
  // A release outranks its prereleases.
  if (pa.pre === pb.pre) return 0;
  if (!pa.pre) return 1;
  if (!pb.pre) return -1;
  // Dot-separated identifiers, digits compared as numbers (rc10 > rc9).
  const ia = pa.pre.replace(/^[-.]/, '').split('.');
  const ib = pb.pre.replace(/^[-.]/, '').split('.');
  for (let i = 0; i < Math.max(ia.length, ib.length); i++) {
    if (ia[i] === undefined) return -1;
    if (ib[i] === undefined) return 1;
    const c = ia[i].localeCompare(ib[i], undefined, { numeric: true });
    if (c !== 0) return c;
  }
  return 0;
};

export const lagOf = (version: string, newest: string): Lag => {
  const v = parseVersion(version);
  const n = parseVersion(newest);
  if (!v || !n) return version === newest ? 'current' : 'unknown';
  if (compareVersions(version, newest) >= 0) return 'current';
  if (v.parts[0] !== n.parts[0]) return 'major';
  if (v.parts[1] !== n.parts[1]) return 'minor';
  return 'patch';
};

const newestOf = (versions: string[]): string =>
  versions.reduce((best, v) => (best === '' || compareVersions(v, best) > 0 ? v : best), '');

// ---------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------

/** Split `host/ns/name/provider//sub/path` at the `//` that isn't a URL scheme's. */
export const splitSource = (source: string): { base: string; subpath: string } => {
  const m = /(?<!:)\/\//.exec(source);
  if (!m) return { base: source, subpath: '' };
  return { base: source.slice(0, m.index), subpath: source.slice(m.index + 2) };
};

/** `spacelift.io/jobnimbus/tf-module-ecs/aws` → `tf-module-ecs`. */
export const moduleName = (base: string): string => {
  const parts = base.split('/').filter(Boolean);
  if (parts.length === 4 && !base.includes('::')) return parts[2];
  return parts[parts.length - 1] || base;
};

const jobKeyOf = (app: string, jobId: string) => `${app}/${jobId}`;

const emptyLagCounts = (): Record<Lag, number> => ({ current: 0, patch: 0, minor: 0, major: 0, unknown: 0 });

// ---------------------------------------------------------------------------
// Roll-up
// ---------------------------------------------------------------------------

/**
 * `published` is getModuleVersions' answers keyed by module source (without a
 * `//subpath`); a module it lacks, or that the registry couldn't answer for,
 * falls back to the newest version deployed.
 */
export const buildFleetModules = (
  input: JobStacks[],
  published: Map<string, ModuleVersionsEntry> = new Map()
): FleetModules => {
  type Raw = Omit<ModuleUsage, 'lag'> & { base: string };
  const raw: Raw[] = [];
  const edgeJobs = new Map<string, Set<string>>();
  const providerRows = new Map<string, Array<{ version: string; job: string }>>();
  const envRegions = new Set<string>();
  let stackRows = 0;
  let localCalls = 0;

  input.forEach(({ app, jobId, stacks }) => {
    const job = jobKeyOf(app, jobId);
    stacks.forEach(stack => {
      stackRows += 1;
      envRegions.add(stack.env_region);
      const sourceByKey = new Map<string, string>();
      (stack.modules || []).forEach(m => sourceByKey.set(m.key, m.source));

      (stack.modules || []).forEach(m => {
        if (!m.version) {
          localCalls += 1;
          return;
        }
        const { base, subpath } = splitSource(m.source);
        const direct = !m.declared_in || m.declared_in === 'root';
        raw.push({
          app,
          jobId,
          envRegion: stack.env_region,
          key: m.key,
          version: m.version,
          constraint: m.constraint,
          declaredIn: m.declared_in,
          direct,
          subpath,
          appliedAt: stack.applied_at,
          base,
        });
        if (!direct) {
          const parentSource = sourceByKey.get(m.declared_in);
          if (parentSource) {
            const parent = splitSource(parentSource).base;
            const edgeKey = `${parent}\n${base}`;
            if (!edgeJobs.has(edgeKey)) edgeJobs.set(edgeKey, new Set());
            edgeJobs.get(edgeKey)!.add(job);
          }
        }
      });

      (stack.providers || []).forEach(p => {
        if (!providerRows.has(p.address)) providerRows.set(p.address, []);
        providerRows.get(p.address)!.push({ version: p.version, job });
      });
    });
  });

  // Group by module.
  const byBase = new Map<string, Raw[]>();
  raw.forEach(r => {
    if (!byBase.has(r.base)) byBase.set(r.base, []);
    byBase.get(r.base)!.push(r);
  });

  const edges: ModuleEdge[] = Array.from(edgeJobs.entries())
    .map(([k, jobs]) => {
      const [parent, child] = k.split('\n');
      return { parent, child, jobs: jobs.size };
    })
    .sort((a, b) => b.jobs - a.jobs || a.parent.localeCompare(b.parent));

  const lagTotals = emptyLagCounts();
  const modules: ModuleSummary[] = Array.from(byBase.entries()).map(([base, rows]) => {
    const newestDeployed = newestOf(rows.map(r => r.version));
    const reg = published.get(base);
    const fromRegistry = !!reg && !reg.error && !!reg.latest;
    const newest = fromRegistry ? reg!.latest : newestDeployed;
    const usages: ModuleUsage[] = rows.map(({ base: _b, ...r }) => ({ ...r, lag: lagOf(r.version, newest) }));

    const versionJobs = new Map<string, { usages: number; jobs: Set<string> }>();
    const lagCounts = emptyLagCounts();
    usages.forEach(u => {
      if (!versionJobs.has(u.version)) versionJobs.set(u.version, { usages: 0, jobs: new Set() });
      const b = versionJobs.get(u.version)!;
      b.usages += 1;
      b.jobs.add(jobKeyOf(u.app, u.jobId));
      if (u.direct) {
        lagCounts[u.lag] += 1;
        lagTotals[u.lag] += 1;
      }
    });

    const versions: VersionBucket[] = Array.from(versionJobs.entries())
      .map(([version, b]) => ({ version, usages: b.usages, jobs: b.jobs.size, lag: lagOf(version, newest) }))
      .sort((a, b) => compareVersions(b.version, a.version));

    const uniq = (xs: string[]) => Array.from(new Set(xs)).sort();
    return {
      source: base,
      name: moduleName(base),
      usages,
      directUsages: usages.filter(u => u.direct).length,
      jobs: uniq(usages.map(u => jobKeyOf(u.app, u.jobId))),
      apps: uniq(usages.map(u => u.app)),
      versions,
      newest,
      newestDeployed,
      latestSource: fromRegistry ? 'registry' : 'deployed',
      published: reg && !reg.error ? reg.versions : [],
      registryError: reg?.error,
      lagCounts,
      constraints: uniq(usages.map(u => u.constraint).filter(Boolean)),
      subpaths: uniq(usages.map(u => u.subpath).filter(Boolean)),
      parents: uniq(edges.filter(e => e.child === base).map(e => e.parent)),
      children: uniq(edges.filter(e => e.parent === base).map(e => e.child)),
    };
  });
  modules.sort((a, b) => b.jobs.length - a.jobs.length || a.name.localeCompare(b.name));

  const providers: ProviderSummary[] = Array.from(providerRows.entries())
    .map(([address, rows]) => {
      const newest = newestOf(rows.map(r => r.version));
      const counts = new Map<string, number>();
      rows.forEach(r => counts.set(r.version, (counts.get(r.version) || 0) + 1));
      return {
        address,
        label: providerLabel(address),
        newest,
        stacks: rows.length,
        jobs: new Set(rows.map(r => r.job)).size,
        versions: Array.from(counts.entries())
          .map(([version, stacks]) => ({ version, stacks, lag: lagOf(version, newest) }))
          .sort((a, b) => compareVersions(b.version, a.version)),
      };
    })
    .sort((a, b) => b.jobs - a.jobs || a.label.localeCompare(b.label));

  // Env drift: a direct module call whose version differs between a job's env/regions.
  const callVersions = new Map<string, Raw[]>();
  raw
    .filter(r => r.direct)
    .forEach(r => {
      const k = `${jobKeyOf(r.app, r.jobId)}\n${r.key}`;
      if (!callVersions.has(k)) callVersions.set(k, []);
      callVersions.get(k)!.push(r);
    });
  const drift: EnvDrift[] = [];
  callVersions.forEach(rows => {
    if (new Set(rows.map(r => r.version)).size < 2) return;
    const r0 = rows[0];
    drift.push({
      app: r0.app,
      jobId: r0.jobId,
      key: r0.key,
      source: r0.base,
      byEnv: rows
        .map(r => ({ envRegion: r.envRegion, version: r.version }))
        .sort((a, b) => a.envRegion.localeCompare(b.envRegion)),
    });
  });
  drift.sort((a, b) => a.app.localeCompare(b.app) || a.jobId.localeCompare(b.jobId) || a.key.localeCompare(b.key));

  const directCalls = raw.filter(r => r.direct).length;
  return {
    modules,
    providers,
    drift,
    edges,
    lagTotals,
    stats: {
      jobs: input.filter(j => j.stacks.length > 0).length,
      stackRows,
      envRegions: envRegions.size,
      modules: modules.length,
      calls: raw.length,
      directCalls,
      localCalls,
      currentShare: directCalls > 0 ? lagTotals.current / directCalls : 0,
      fragmented: modules.filter(m => m.versions.length > 1).length,
      registryModules: modules.filter(m => m.latestSource === 'registry').length,
    },
  };
};

// ---------------------------------------------------------------------------
// Adoption matrix: modules × apps
// ---------------------------------------------------------------------------

export interface AdoptionCell {
  versions: string[];
  /** The worst lag among the app's direct calls (any call when it has none). */
  lag: Lag;
  usages: number;
}

export interface AdoptionMatrix {
  apps: string[];
  rows: Array<{ module: ModuleSummary; cells: Record<string, AdoptionCell> }>;
}

const worstLag = (lags: Lag[]): Lag => {
  const known = lags.filter(l => l !== 'unknown');
  if (known.length === 0) return 'unknown';
  return known.reduce((w, l) => (LAG_ORDER.indexOf(l) > LAG_ORDER.indexOf(w) ? l : w), 'current' as Lag);
};

export const buildAdoptionMatrix = (fleet: FleetModules, opts: { directOnly?: boolean } = {}): AdoptionMatrix => {
  const appCounts = new Map<string, number>();
  const rows = fleet.modules
    .map(module => {
      const usages = opts.directOnly ? module.usages.filter(u => u.direct) : module.usages;
      const byApp = new Map<string, typeof usages>();
      usages.forEach(u => {
        if (!byApp.has(u.app)) byApp.set(u.app, []);
        byApp.get(u.app)!.push(u);
      });
      const cells: Record<string, AdoptionCell> = {};
      byApp.forEach((us, app) => {
        appCounts.set(app, (appCounts.get(app) || 0) + 1);
        cells[app] = {
          versions: Array.from(new Set(us.map(u => u.version))).sort((a, b) => compareVersions(b, a)),
          lag: worstLag(us.map(u => u.lag)),
          usages: us.length,
        };
      });
      return { module, cells };
    })
    .filter(r => Object.keys(r.cells).length > 0);

  // Widest-adopting apps first so the dense corner is top-left.
  const apps = Array.from(appCounts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([app]) => app);
  return { apps, rows };
};

// ---------------------------------------------------------------------------
// App → module flows (for the flow diagram)
// ---------------------------------------------------------------------------

export const OTHER_APPS = '__other_apps__';
export const OTHER_MODULES = '__other_modules__';

export interface AppModuleFlows {
  /** Ordered by total, OTHER_APPS last when present. */
  apps: Array<{ id: string; label: string; value: number }>;
  modules: Array<{ id: string; label: string; value: number }>;
  /** `value` is distinct jobs of the app calling the module directly. */
  links: Array<{ app: string; module: string; value: number }>;
}

/**
 * Distinct jobs per app × module over direct calls, keeping the biggest
 * `topApps` apps and `topModules` modules and folding the rest into "Other".
 */
export const buildAppModuleFlows = (
  fleet: FleetModules,
  opts: { topApps?: number; topModules?: number } = {}
): AppModuleFlows => {
  const topApps = opts.topApps ?? 10;
  const topModules = opts.topModules ?? 12;

  const pairs = new Map<string, Set<string>>();
  fleet.modules.forEach(m =>
    m.usages
      .filter(u => u.direct)
      .forEach(u => {
        const k = `${u.app}\n${m.source}`;
        if (!pairs.has(k)) pairs.set(k, new Set());
        pairs.get(k)!.add(u.jobId);
      })
  );

  const appTotals = new Map<string, number>();
  const moduleTotals = new Map<string, number>();
  pairs.forEach((jobs, k) => {
    const [app, source] = k.split('\n');
    appTotals.set(app, (appTotals.get(app) || 0) + jobs.size);
    moduleTotals.set(source, (moduleTotals.get(source) || 0) + jobs.size);
  });

  const rank = (totals: Map<string, number>) =>
    Array.from(totals.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const keptApps = new Set(rank(appTotals).slice(0, topApps).map(([a]) => a));
  const keptModules = new Set(rank(moduleTotals).slice(0, topModules).map(([m]) => m));
  const appId = (a: string) => (keptApps.has(a) ? a : OTHER_APPS);
  const moduleId = (m: string) => (keptModules.has(m) ? m : OTHER_MODULES);

  const linkTotals = new Map<string, number>();
  pairs.forEach((jobs, k) => {
    const [app, source] = k.split('\n');
    const lk = `${appId(app)}\n${moduleId(source)}`;
    linkTotals.set(lk, (linkTotals.get(lk) || 0) + jobs.size);
  });
  const links = Array.from(linkTotals.entries()).map(([k, value]) => {
    const [app, module] = k.split('\n');
    return { app, module, value };
  });

  const sideTotals = (pick: (l: { app: string; module: string }) => string) => {
    const t = new Map<string, number>();
    links.forEach(l => t.set(pick(l), (t.get(pick(l)) || 0) + l.value));
    return t;
  };
  const order = (totals: Map<string, number>, other: string, label: (id: string) => string, otherCount: number) =>
    Array.from(totals.entries())
      .sort((a, b) => Number(a[0] === other) - Number(b[0] === other) || b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([id, value]) => ({ id, value, label: id === other ? `${otherCount} other` : label(id) }));

  return {
    apps: order(sideTotals(l => l.app), OTHER_APPS, a => a, appTotals.size - keptApps.size),
    modules: order(sideTotals(l => l.module), OTHER_MODULES, moduleName, moduleTotals.size - keptModules.size),
    links,
  };
};

/** Route for a module's detail page; the source's slashes become path segments. */
export const modulePath = (source: string): string =>
  `/infra/modules/${source.split('/').map(encodeURIComponent).join('/')}`;
